/**
 * API / WebSocket origin helpers for Capacitor native builds.
 * On web, relative paths and window.location are correct; on native the
 * WebView origin is https://localhost so absolute URLs must come from env.
 */

/** Production API host — native builds that point here must never show a DEV badge. */
const PROD_API_HOSTS = new Set([
  'api.fivestarjob.ca',
])

export function getApiBaseUrl() {
  return (import.meta.env?.VITE_API_BASE_URL || '').replace(/\/+$/, '')
}

/** Hostname from VITE_API_BASE_URL (empty on same-origin web builds). */
export function getApiHost() {
  const base = getApiBaseUrl()
  if (!base) return ''
  try {
    return new URL(base).hostname.toLowerCase()
  } catch {
    return ''
  }
}

/**
 * True when the compile-time API base is not production.
 * Used for native-only DEV indicators (web uses the page hostname instead).
 */
export function isNonProductionApi() {
  const host = getApiHost()
  if (!host) return false
  return !PROD_API_HOSTS.has(host)
}

/** Short label for the native DEV banner, e.g. "DEV · api-dev.fivestarjob.com". */
export function getNonProductionApiLabel() {
  const host = getApiHost()
  if (!host || PROD_API_HOSTS.has(host)) return ''
  if (host.includes('api-dev') || host.startsWith('dev.')) return `DEV · ${host}`
  if (host === 'localhost' || host === '127.0.0.1') return `DEV · ${host}`
  return `NON-PROD · ${host}`
}

/** Build ws(s) URL for a path on the API host (or same-origin on web). */
export function buildApiWebSocketUrl(path, search = '') {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const base = getApiBaseUrl()
  if (base) {
    const wsOrigin = base.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:')
    return `${wsOrigin}${normalizedPath}${search}`
  }
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${window.location.host}${normalizedPath}${search}`
}
